/**
 * Адаптер среды (ТЗ стр. 23): экраны спрашивают возможности у него, а не
 * проверяют window.Telegram по всему коду. Три среды - Telegram Mini App,
 * MAX Mini App и PWA - отличаются входом в Штаб, кнопкой «назад», темой и
 * откликом вибрацией.
 */
import type { HubBotChannel, HubContextRef } from '../data/types'

export type PlatformKind = 'telegram' | 'max' | 'web'

/** Что открыть в Штабе: экран, с которого пришёл пользователь. */
export interface HubContext {
  /** Подпись для человека: «Стрижи, тренировка 1 октября, 17:00» */
  label: string
  /** Карточка «Обсудить в боте» (S16): «Стрижи» */
  title?: string
  /** Под заголовком карточки: «Тренировка 1 октября, 17:00» */
  detail?: string
  /**
   * Объект для кода перехода в бота. В ссылку на бота уходит только
   * одноразовый код, ID и имена остаются на сервере.
   */
  ref?: HubContextRef
}

export interface Platform {
  kind: PlatformKind
  /** В мини-приложениях Штаб - это бот, в PWA - собственный чат. */
  hubMode: 'bot' | 'chat'
  colorScheme(): 'light' | 'dark'
  /**
   * Системная кнопка «назад» мини-приложения. Возвращает функцию снятия.
   * В PWA ничего не делает: там работает история браузера.
   */
  useBackButton(onBack: () => void): () => void
  /** Открыть Штаб без контекста. Для чата PWA возвращает false - навигацию делает экран. */
  openHub(context?: HubContext): boolean
  /** Бот Штаба этой среды; в PWA null. */
  botChannel: HubBotChannel | null
  /**
   * Ссылка на бота, с параметром start (код контекста ctx_...). null - бота
   * нет или имя бота не задано.
   */
  botLink(startParam?: string): string | null
  /**
   * Открыть ссылку на бота средствами мессенджера. Звать только из
   * обработчика нажатия: MAX открывает ссылку лишь после действия
   * пользователя. false - открыть нечем.
   */
  openBotLink(url: string): boolean
  haptic(kind: 'select' | 'success' | 'error'): void
  /**
   * Открыть страницу оплаты (S19). Telegram и MAX - внешний браузер средствами
   * мессенджера (openLink), мини-приложение остаётся открытым. PWA - переход в
   * этой же вкладке: всплывающее окно после запроса к серверу браузер
   * заблокирует, а платёжная страница вернёт по return_url. В установленной
   * PWA на iOS страница откроется во встроенном окне браузера поверх
   * приложения - статус платежа экран перечитывает при возврате. Звать из
   * обработчика нажатия. false - открыть нечем.
   */
  openPayment(url: string): boolean
  /**
   * Возврат в мини-приложение (SC12): пользователь ушёл в бота или свернул
   * мессенджер и вернулся. onBack зовётся один раз на возврат. Возвращает
   * функцию снятия. В PWA ничего не делает.
   */
  onResume(onBack: () => void): () => void
}

/** Подписка на события моста Telegram (Bot API 8.0+: activated, deactivated). */
interface BridgeEvents {
  onEvent?(eventType: string, cb: () => void): void
  offEvent?(eventType: string, cb: () => void): void
}

interface TelegramLike extends BridgeEvents {
  initData?: string
  colorScheme?: 'light' | 'dark'
  BackButton?: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void }
  HapticFeedback?: {
    selectionChanged(): void
    notificationOccurred(type: 'success' | 'error' | 'warning'): void
  }
  openTelegramLink?(url: string): void
  /** Внешняя ссылка во внешнем браузере, мини-приложение не закрывается. */
  openLink?(url: string): void
}

/** Мост MAX: window.WebApp из https://st.max.ru/js/max-web-app.js */
interface MaxLike {
  initData?: string
  BackButton?: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void }
  HapticFeedback?: {
    selectionChanged(): void
    notificationOccurred(type: 'success' | 'error' | 'warning'): void
  }
  openMaxLink?(url: string): void
  /** Внешняя ссылка (max-web-app.js: событие WebAppOpenLink). */
  openLink?(url: string): void
}

export interface PlatformConfig {
  telegramBot: string
  maxBot: string
}

/**
 * Ники ботов ProCoach. Единственное место во фронте: им пользуются и
 * мини-приложения (Штаб - бот), и вход в PWA через бота.
 */
export const DEFAULT_PLATFORM_CONFIG: PlatformConfig = {
  telegramBot: 'ProCoach_AI_bot',
  maxBot: 'id343516070949_1_bot',
}

const DEFAULT_CONFIG = DEFAULT_PLATFORM_CONFIG

function prefersDark(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches
}

/**
 * Ссылка на бота. Telegram: https://t.me/<бот>?start=<параметр> (до 64
 * символов [A-Za-z0-9_-]). MAX: https://max.ru/<бот>?start=<параметр>, бот
 * получает его в bot_started.payload (до 128 символов).
 */
export function botLinkFor(kind: HubBotChannel, botName: string, startParam?: string): string | null {
  if (!botName) return null
  const base = kind === 'telegram' ? `https://t.me/${botName}` : `https://max.ru/${botName}`
  return startParam ? `${base}?start=${encodeURIComponent(startParam)}` : base
}

/**
 * Ссылка, которая открывает приложение мессенджера напрямую, минуя браузер:
 * Telegram - tg://resolve?domain=<бот>&start=<параметр>, MAX -
 * max://max.ru/<бот>?start=<параметр> (так её строит сама страница max.ru).
 * Нужна установленной PWA на iOS: https-ссылка открывается там во встроенном
 * окне браузера поверх приложения, и после возврата тренер видит пустое окно.
 */
export function botAppLinkFor(kind: HubBotChannel, botName: string, startParam?: string): string | null {
  if (!botName) return null
  if (kind === 'telegram') {
    const q = new URLSearchParams({ domain: botName })
    if (startParam) q.set('start', startParam)
    return `tg://resolve?${q.toString()}`
  }
  return startParam ? `max://max.ru/${botName}?start=${encodeURIComponent(startParam)}` : `max://max.ru/${botName}`
}

/**
 * Возврат в мини-приложение. Источники:
 * - visibilitychange документа: страница скрыта, пока открыт чат с ботом или
 *   мессенджер свёрнут (общий для Telegram и MAX; у моста MAX событий
 *   жизненного цикла нет - проверено по max-web-app.js);
 * - события моста Telegram deactivated/activated (Bot API 8.0+): мини-приложение
 *   свернули в панель, а страница при этом может остаться «видимой».
 * onBack зовётся, только если перед этим был уход: оба источника на один
 * возврат дают один вызов, событие без ухода (запуск) - ни одного.
 */
export function subscribeResume(onBack: () => void, bridge?: BridgeEvents): () => void {
  if (typeof document === 'undefined') return () => {}
  let away = document.visibilityState === 'hidden'
  const leave = () => {
    away = true
  }
  const back = () => {
    if (!away) return
    away = false
    onBack()
  }
  const onVisibility = () => (document.visibilityState === 'hidden' ? leave() : back())
  document.addEventListener('visibilitychange', onVisibility)

  const bridgeOn = (type: string, cb: () => void) => {
    try {
      bridge?.onEvent?.(type, cb)
    } catch {
      // старый клиент не знает событие - остаётся visibilitychange
    }
  }
  const bridgeOff = (type: string, cb: () => void) => {
    try {
      bridge?.offEvent?.(type, cb)
    } catch {
      // снять нечего
    }
  }
  bridgeOn('deactivated', leave)
  bridgeOn('activated', back)

  return () => {
    document.removeEventListener('visibilitychange', onVisibility)
    bridgeOff('deactivated', leave)
    bridgeOff('activated', back)
  }
}

function bridgePlatform(
  kind: 'telegram' | 'max',
  bridge: TelegramLike & MaxLike,
  openLink: (url: string) => void,
  botName: string,
): Platform {
  const botUrl = botLinkFor(kind, botName)
  const open = (url: string) => {
    try {
      openLink(url)
      return true
    } catch {
      return false
    }
  }
  return {
    kind,
    hubMode: 'bot',
    botChannel: kind,
    botLink: (startParam) => botLinkFor(kind, botName, startParam),
    openBotLink: open,
    colorScheme: () => bridge.colorScheme ?? (prefersDark() ? 'dark' : 'light'),
    useBackButton(onBack) {
      const button = bridge.BackButton
      if (!button) return () => {}
      button.onClick(onBack)
      button.show()
      return () => {
        button.offClick(onBack)
        button.hide()
      }
    },
    openHub() {
      // Без контекста - просто открыть бота. Контекст передаёт экран
      // «Обсудить в боте» одноразовым кодом (S16), команды и имена в ссылку не
      // кладём (ТЗ стр. 6).
      return botUrl ? open(botUrl) : false
    },
    openPayment(url) {
      try {
        if (bridge.openLink) bridge.openLink(url)
        else if (!window.open(url, '_blank')) return false
        return true
      } catch {
        return false
      }
    },
    haptic(k) {
      const h = bridge.HapticFeedback
      if (!h) return
      if (k === 'select') h.selectionChanged()
      else h.notificationOccurred(k)
    },
    // У MAX своих событий жизненного цикла нет: только видимость страницы.
    onResume: (onBack) => subscribeResume(onBack, kind === 'telegram' ? bridge : undefined),
  }
}

export function webPlatform(): Platform {
  return {
    kind: 'web',
    hubMode: 'chat',
    colorScheme: () => (prefersDark() ? 'dark' : 'light'),
    useBackButton: () => () => {},
    openHub: () => false,
    botChannel: null,
    botLink: () => null,
    openBotLink: () => false,
    openPayment(url) {
      try {
        window.location.assign(url)
        return true
      } catch {
        return false
      }
    },
    haptic: () => {},
    // PWA: вернуться «из бота» нельзя, Штаб - свой чат.
    onResume: () => () => {},
  }
}

/** Определить среду по тому, какой мост подписал запуск. */
export function detectPlatform(config: Partial<PlatformConfig> = {}): Platform {
  const cfg = { ...DEFAULT_CONFIG, ...config }
  const w = window as unknown as { Telegram?: { WebApp?: TelegramLike }; WebApp?: MaxLike }

  const tg = w.Telegram?.WebApp
  if (tg?.initData) {
    return bridgePlatform('telegram', tg, (u) => (tg.openTelegramLink ? tg.openTelegramLink(u) : window.open(u, '_blank')), cfg.telegramBot)
  }

  const max = w.WebApp
  if (max?.initData) {
    return bridgePlatform('max', max, (u) => (max.openMaxLink ? max.openMaxLink(u) : window.open(u, '_blank')), cfg.maxBot)
  }

  return webPlatform()
}
